/**
 * The UI's entry point to the command registry: menus, palette entries and
 * dialogs call `runCommand` instead of building native commands inline, so
 * the user and agents run the very same definitions (ADR-009 §3.4, G15).
 *
 * Synchronous commands go through `dispatchUser` (same task, native
 * selection, the UI's labels). Commands that prepare asynchronously (workers,
 * decoders) go through the dispatcher envelope as the user.
 */
import type { TransactionResult } from '@maipo/agent-protocol';
import type { DocumentSession } from '../../core/session';
import { dispatchUser, localTxId, type LocalActor, type UserDispatchOutcome } from '../user-dispatch';
import { editorCommands } from './index';

/**
 * `label` overrides the command's history label where one command backs UI
 * entries that were labelled differently (the Note tool's "Pin note").
 */
export function runCommand<P extends object>(session: DocumentSession, id: string, params: P, actor: LocalActor = { kind: 'user' }, label?: string): UserDispatchOutcome {
  const command = editorCommands().get(id);
  if (!command) return { status: 'rejected', code: 'UNSUPPORTED_COMMAND', message: `Command ${id} does not exist.` };
  if (command.asynchronous) return { status: 'rejected', code: 'COMMAND_REJECTED', message: `Command ${id} prepares asynchronously; use runCommandAsync.` };
  const record = params as Record<string, unknown>;
  return dispatchUser(session, [{ type: 'command.run', command: id, params: record }], label ?? command.label(record, session.document), actor);
}

export async function runCommandAsync<P extends object>(session: DocumentSession, id: string, params: P, signal?: AbortSignal): Promise<TransactionResult> {
  const command = editorCommands().get(id);
  const { semanticRuntime } = await import('../dispatcher');
  const runtime = semanticRuntime(session);
  const record = params as Record<string, unknown>;
  return runtime.dispatch({
    txId: localTxId('user'),
    document: 'local',
    baseRevision: session.currentRevision,
    label: command ? command.label(record, session.document) : id,
    intents: [{ type: 'command.run', command: id, params: record }],
  }, { kind: 'user' }, signal);
}
